import { useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { useOnboard, useNonce } from "@mormlayer/hooks";
import { useAccount } from "wagmi";
import { Dashboard } from "./components/Dashboard";
import { CreatePoolForm } from "./components/CreatePoolForm";
import { LaunchPerpForm } from "./components/LaunchPerpForm";
import { PoolDetails } from "./components/PoolDetails";
import { AddLiquidityForm } from "./components/AddLiquidityForm";
import { LivePoolDashboard } from "./components/LivePoolDashboard";
import type { CreatePoolFormState, PoolInfo } from "./types/pool";
import type { LaunchPerpFormState } from "./types/perp";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "https://api.morpheum.io";

type Step = 1 | 2 | 3 | 4 | 5 | 6;
type FormMode = "pool" | "perp";

function App() {
  const [step, setStep] = useState<Step>(1);
  const [formMode, setFormMode] = useState<FormMode>("pool");
  const [createForm, setCreateForm] = useState<CreatePoolFormState | null>(null);
  const [perpForm, setPerpForm] = useState<LaunchPerpFormState | null>(null);
  const [pool, setPool] = useState<PoolInfo | null>(null);

  const { walletAddress } = useOnboard();
  const { address: wagmiAddress } = useAccount();
  useNonce(BASE_URL, (walletAddress || wagmiAddress) ?? undefined);

  const handleCreateNewPool = useCallback(() => {
    setCreateForm(null);
    setPerpForm(null);
    setPool(null);
    setFormMode("pool");
    setStep(2);
  }, []);

  const handleLaunchPerpMarket = useCallback(() => {
    setCreateForm(null);
    setPerpForm(null);
    setPool(null);
    setFormMode("perp");
    setStep(2);
  }, []);

  const handleSubmitProposal = useCallback((form: CreatePoolFormState) => {
    setCreateForm(form);
    const poolId = `0x${Array.from({ length: 40 }, () =>
      Math.floor(Math.random() * 16).toString(16)
    ).join("")}`;
    setPool({
      poolId,
      tokenA: form.tokenA,
      tokenB: form.tokenB,
      poolMode: form.poolMode,
      ...(form.pythPriceFeedId != null && { pythPriceFeedId: form.pythPriceFeedId }),
      feeTierPct: form.feeTierPct,
      ...(form.feeRebatePct != null && form.feeRebatePct > 0 && { feeRebatePct: form.feeRebatePct }),
      tickSpacing: form.tickSpacing,
      reclamm: form.reclamm,
      boosted: form.boosted,
      hooks: [],
      createdAt: Date.now(),
    });
    setStep(3);
  }, []);

  const handleBackFromForm = useCallback(() => {
    setStep(1);
    setFormMode("pool");
  }, []);

  const handleSubmitPerpProposal = useCallback((form: LaunchPerpFormState) => {
    setPerpForm(form);
    toast.success(
      `Perp proposal: ${form.baseAsset}-${form.quoteAsset}-PERP. Submit to API when integrated.`
    );
    setStep(6);
  }, []);

  const handleDonePerp = useCallback(() => {
    setStep(1);
    setPerpForm(null);
  }, []);
  const handleBackFromDetails = useCallback(() => setStep(2), []);
  const handleBackFromLiquidity = useCallback(() => setStep(3), []);
  const handleAddLiquidity = useCallback(() => setStep(4), []);
  const handleLiquiditySuccess = useCallback(() => setStep(5), []);
  const handleDone = useCallback(() => {
    setStep(1);
    setPool(null);
    setCreateForm(null);
  }, []);

  return (
    <>
      {step === 1 && (
        <Dashboard
          apiBaseUrl={BASE_URL}
          onCreateNewPool={handleCreateNewPool}
          onLaunchPerpMarket={handleLaunchPerpMarket}
        />
      )}
      {step === 2 && formMode === "pool" && (
        <CreatePoolForm
          initial={createForm ?? undefined}
          onSubmit={handleSubmitProposal}
          onBack={handleBackFromForm}
        />
      )}
      {step === 2 && formMode === "perp" && (
        <LaunchPerpForm
          initial={perpForm ?? undefined}
          onSubmit={handleSubmitPerpProposal}
          onBack={handleBackFromForm}
        />
      )}
      {step === 3 && pool && (
        <PoolDetails
          pool={pool}
          onAddLiquidity={handleAddLiquidity}
          onBack={handleBackFromDetails}
        />
      )}
      {step === 4 && pool && (
        <AddLiquidityForm
          pool={pool}
          onSuccess={handleLiquiditySuccess}
          onBack={handleBackFromLiquidity}
        />
      )}
      {step === 5 && pool && <LivePoolDashboard pool={pool} onDone={handleDone} />}
      {step === 6 && perpForm && (
        <div className="screen" style={{ padding: "1.5rem" }}>
          <header className="form-header" style={{ marginBottom: "1.5rem" }}>
            <h1 style={{ margin: 0 }}>Perp Proposal Submitted</h1>
          </header>
          <div
            style={{
              maxWidth: 520,
              padding: "1.5rem",
              background: "var(--bg-card)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
            }}
          >
            <p style={{ margin: "0 0 1rem" }}>
              Market: <strong>{perpForm.baseAsset}-{perpForm.quoteAsset}-PERP</strong>
            </p>
            <p style={{ margin: "0 0 1rem", fontSize: "0.9rem", color: "var(--text-muted)" }}>
              Tick size: {perpForm.tickSizeSatoshi} satoshi · Min / Max size:{" "}
              {perpForm.minOrderSize} / {perpForm.maxOrderSize}
            </p>
            <p style={{ margin: "0 0 1rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
              API integration will submit this to the market module (CreateMarket / PushMarketListing). Once approved and activated, the market will appear in the market list and be tradeable on the CLOB.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={handleDonePerp}
                style={{
                  padding: "0.6rem 1.25rem",
                  border: "none",
                  borderRadius: 6,
                  background: "var(--accent)",
                  color: "var(--bg)",
                  fontWeight: 600,
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "var(--bg-card)",
            color: "var(--text)",
            border: "1px solid var(--border)",
          },
        }}
      />
    </>
  );
}

export default App;
